/*
 * Sign in with Google  ·  assets/hoh-google-signin.js  ·  HALL-GOOGLE-01 (Jan, 2026-10-23)
 *
 * Jan: "setting up, like, a Google sign-in so they can just sign in with Google if that's easier for a lot of people
 * to make accounts and stuff and submit their characters."
 *
 * The flow, all on framedfantasy.com plus Google:
 *   1. A "Continue with Google" button (any element with data-hoh-google) sends the visitor to Google with a PKCE
 *      challenge, a state and a nonce. The verifier, state and nonce stay in this browser (localStorage).
 *   2. Google sends them back to the same Hall, hero or commission page with ?code=&state=. This file clears those
 *      from the address at once, checks the state, and posts the code and verifier to the ff-hall-google Worker.
 *      (The add-hero form and the commission page's dialog send Google to a window of its own instead, which hands
 *      the answer back through localStorage, so the page keeps what was typed.)
 *   3. The Worker swaps them at Google for the ID token, checks it, and has Supabase open a Hall session:
 *      'g1.' + a secret. The Hall keeps that in hoh-your-hall-token where it keeps a PIN, and every Hall door that
 *      checks a PIN accepts it (sql/HALL-GOOGLE-01-UPDATE.sql).
 *   4. An email with no hero yet keeps its session aside (hoh-google-new) until the first hero is submitted, so the
 *      Hall never treats it as signed in with nothing to show (hoh-your-hall.js would sign it out again).
 *
 * Why a redirect and not Google's pop-up: on iPhone the pop-up can come back to a blank page (Safari's tracking
 * prevention), and most players are on phones. Why a one-time code and not the ID token in the address: the Meta
 * pixel and GTM read the address on load; a code is useless to them without this browser's verifier and the
 * Worker's client secret.
 *
 * Google refuses sign-in inside app browsers (Instagram, Facebook, TikTok and other WebViews), so the button never
 * shows there; the email and PIN sign-in is unchanged. Until the Worker has its settings, /config does not answer
 * and nothing here shows at all.
 *
 * Public API (window.hohGoogle): available(), ready(), fill(root), start(opts), onResult(fn), pendingNew(),
 * takeNew(email), saveSession(email, pin, name), setPin(email, session, pin), hint(email), inApp(), message(reason),
 * sessionFor(email) (this browser's Google session for that email, for the commission page's past.hall.link).
 */
(function(){
  'use strict';
  var W = window, D = document;
  if(W.hohGoogle) return;

  /* The three addresses can be pointed elsewhere only by a page that sets them before this file loads (the local
     harness, harness/hall-google/serve.mjs); the live pages never do. */
  var WORKER = W.HOH_GOOGLE_WORKER || 'https://ff-hall-google.fondlyframed.workers.dev';
  var SB_URL = W.HOH_GOOGLE_SB || 'https://obadckrukccwkjgpbsxa.supabase.co';
  var AUTH_URL = W.HOH_GOOGLE_AUTH || 'https://accounts.google.com/o/oauth2/v2/auth';
  var SB_KEY = 'sb_publishable_4DntR53dZydUazuyuPI3YQ_zg69PLYZ';   /* the Hall's public key (hoh-section-core.js) */
  var CFG_KEY = 'hoh-google-cfg', PENDING_KEY = 'hoh-google-pending', NEW_KEY = 'hoh-google-new';
  var RESULT_KEY = 'hoh-google-result';                               /* sessionStorage, across one bounce */
  var DONE_KEY = 'hoh-google-done';                                   /* localStorage, from a Google window to the page that opened it */
  var TOKEN_KEY = 'hoh-your-hall-token', EMAIL_KEY = 'hoh-your-hall-email', NAME_KEY = 'hoh-your-hall-name';
  var CFG_MS = 6 * 3600e3, PENDING_MS = 15 * 60e3, NEW_MS = 24 * 3600e3;
  var RETURN_PARAMS = ['code', 'state', 'scope', 'authuser', 'prompt', 'hd', 'iss', 'error', 'error_description', 'error_uri'];

  function now(){ return Date.now(); }
  function readJSON(store, k){ try { var s = store.getItem(k); return s ? JSON.parse(s) : null; } catch(_){ return null; } }
  function writeJSON(store, k, v){ try { store.setItem(k, JSON.stringify(v)); return true; } catch(_){ return false; } }
  function drop(store, k){ try { store.removeItem(k); } catch(_){} }
  var LS = (function(){ try { return W.localStorage; } catch(_){ return null; } })();
  var SS = (function(){ try { return W.sessionStorage; } catch(_){ return null; } })();
  function esc(s){ return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function cleanEmail(e){ return String(e || '').trim().toLowerCase(); }

  /* Google refuses OAuth inside embedded browsers (disallowed_useragent), so those never get the button. */
  function inApp(){
    var ua = (W.navigator && navigator.userAgent) || '';
    if(/FBAN|FBAV|FB_IAB|FBIOS|FB4A|Instagram|Line\/|TikTok|musical_ly|Bytedance|Snapchat|Pinterest|LinkedInApp|GSA\/|; wv\)/i.test(ua)) return true;
    return /iPhone|iPad|iPod/.test(ua) && !/Safari\//.test(ua);
  }
  function canTry(){
    return !!(LS && W.crypto && W.crypto.subtle && W.crypto.getRandomValues && W.fetch && W.URL && W.history && history.replaceState)
      && (/^https:$/.test(location.protocol) || /^(localhost|127\.0\.0\.1)$/.test(location.hostname)) && !inApp();
  }

  /* ── the Worker's public settings (client id, the pages Google may return to), kept six hours ─────────────── */
  var cfgWait = null;
  function cachedCfg(){
    var c = LS && readJSON(LS, CFG_KEY);
    return (c && c.client_id && Array.isArray(c.pages) && now() - (c.at || 0) < CFG_MS) ? c : null;
  }
  function config(){
    if(!canTry()) return Promise.resolve(null);
    var c = cachedCfg();
    if(c) return Promise.resolve(c);
    if(!cfgWait){
      cfgWait = fetch(WORKER + '/config', { credentials: 'omit' })
        .then(function(r){ return r.ok ? r.json() : null; })
        .then(function(j){
          if(!j || j.ok !== true || typeof j.client_id !== 'string' || !Array.isArray(j.pages)) return null;
          var cfg = { client_id: j.client_id, pages: j.pages.filter(function(p){ return typeof p === 'string'; }), at: now() };
          writeJSON(LS, CFG_KEY, cfg);
          return cfg;
        }, function(){ return null; })
        .then(function(cfg){ cfgWait = null; return cfg; });
    }
    return cfgWait;
  }
  /* Google returns to a page Jan registered: this page's own address when it is one, else the Hall's. */
  function landing(cfg){
    if(!cfg) return '';
    var here = location.origin + location.pathname.replace(/\/+$/, '');
    for(var i = 0; i < cfg.pages.length; i++){ if(cfg.pages[i] === here) return here; }
    for(var j = 0; j < cfg.pages.length; j++){ if(cfg.pages[j] === location.origin + '/pages/hall-of-heroes') return cfg.pages[j]; }
    return '';
  }
  function available(){ return canTry() && !!landing(cachedCfg()); }
  function ready(){ return config().then(function(cfg){ return !!landing(cfg); }); }

  /* ── PKCE, state, nonce ──────────────────────────────────────────────────────────────────────────────────── */
  function b64url(bytes){
    var s = ''; for(var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function random(n){ var b = new Uint8Array(n); W.crypto.getRandomValues(b); return b64url(b); }
  function challenge(verifier){
    return W.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)).then(function(d){ return b64url(new Uint8Array(d)); });
  }
  function addressWithout(params){
    var u = new URL(location.href);
    params.forEach(function(k){ u.searchParams.delete(k); });
    return u.pathname + u.search + u.hash;
  }

  /* opts: { from: 'hall' | 'form' | 'hero' | 'portal', email, reset, tab }
     tab: the page must stay as it is (the add-hero form with its picture, the portal's dialog), so Google runs in its own
     window: a small one on a computer, a tab on a phone. That window lands on this page's address, finishes the sign-in
     and hands the answer back here through localStorage (DONE_KEY), then closes itself where the browser lets it. */
  var starting = false, waiting = null;
  function phoneLike(){ try { return matchMedia('(pointer: coarse)').matches || W.innerWidth < 700; } catch(_){ return true; } }
  function start(opts){
    opts = opts || {};
    if(starting) return Promise.resolve(false);
    var tab = null;
    if(opts.tab){
      /* opened now, while the tap is still the browser's to honour; Google's address follows once it is known */
      try {
        var w = 520, h = 680;
        tab = W.open('', 'hoh-google', phoneLike() ? '' : 'popup,width=' + w + ',height=' + h + ',left=' + Math.max(0, Math.round((screen.width - w) / 2)) + ',top=' + Math.max(0, Math.round((screen.height - h) / 3)));
      } catch(_){ tab = null; }
      if(!tab){
        publish({ ok: false, reason: 'blocked', message: message('blocked'), from: opts.from || 'hall' });
        return Promise.resolve(false);
      }
      try { tab.document.title = 'Continue with Google'; tab.document.body.style.cssText = 'margin:0;background:#131314;color:#E3E3E3;font:500 15px/1.5 Arial,sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh'; tab.document.body.textContent = 'Opening Google'; } catch(_){}
    }
    starting = true;
    function giveUp(){ starting = false; if(tab){ try { tab.close(); } catch(_){} } return false; }
    return config().then(function(cfg){
      var page = landing(cfg);
      if(!page) return giveUp();
      var verifier = random(48), state = random(18), nonce = random(18);
      return challenge(verifier).then(function(ch){
        writeJSON(LS, PENDING_KEY, { state: state, verifier: verifier, nonce: nonce, page: page, from: opts.from || 'hall',
          reset: !!opts.reset, tab: !!tab, ret: location.origin + addressWithout(RETURN_PARAMS), at: now() });
        var q = new URLSearchParams({ client_id: cfg.client_id, redirect_uri: page, response_type: 'code', scope: 'openid email profile',
          state: state, nonce: nonce, code_challenge: ch, code_challenge_method: 'S256', prompt: 'select_account' });
        var hint = cleanEmail(opts.email);
        if(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(hint)) q.set('login_hint', hint);
        var url = AUTH_URL + '?' + q.toString();
        if(tab){
          waiting = { state: state, from: opts.from || 'hall', at: now() };
          starting = false;
          tab.location.href = url;
          return true;
        }
        location.assign(url);
        return true;
      });
    }).catch(giveUp);
  }
  /* The page that opened a Google window takes its answer when it arrives, or when the visitor comes back to it. */
  function takeDone(){
    if(!waiting) return;
    var d = LS && readJSON(LS, DONE_KEY);
    if(!d || d.state !== waiting.state) return;
    drop(LS, DONE_KEY);
    var from = waiting.from;
    waiting = null;
    delete d.state;
    d.from = from;
    wake();
    if(d.ok && d.heroes > 0){
      /* the other window kept the session; this page's Hall wakes up to it too */
      var t = LS && readJSON(LS, TOKEN_KEY);
      if(t && cleanEmail(t.email) === d.email && t.pin && typeof W.hohYourHallSetSession === 'function'){ try { W.hohYourHallSetSession(t.email, t.pin); } catch(_){} }
      try { D.dispatchEvent(new CustomEvent('hoh:signed-in', { detail: { source: 'google' } })); } catch(_){}
    }
    publish(d);
  }
  W.addEventListener('storage', function(e){ if(e.key === DONE_KEY && e.newValue) takeDone(); });
  W.addEventListener('focus', takeDone);
  D.addEventListener('visibilitychange', function(){ if(!D.hidden) takeDone(); });

  /* ── what a result means in words (one place for every screen) ───────────────────────────────────────────── */
  function message(reason){
    switch(reason){
      case 'cancel': return '';
      case 'busy': return "Too many Google sign-ins for this email today. Try tomorrow or use your email and PIN.";
      case 'unverified': return "Google hasn’t verified this email. Sign in with your email and PIN instead.";
      case 'blocked': return "Google sign-in was blocked. Allow pop-ups for framedfantasy.com or sign in with email.";
      case 'not_set': case 'not_ready': case 'network': case 'server': case 'google_unreachable': case 'keys_unreachable':
        return "Google sign-in isn’t available. Use your email and PIN, or try again later.";
      default: return "Google sign-in didn’t finish. Try again or use your email and PIN.";
    }
  }

  /* ── keeping the session ─────────────────────────────────────────────────────────────────────────────────── */
  /* The same token the Hall writes (hoh-your-hall.js setStoredToken): email, pin, savedAt, phone. */
  function saveSession(email, pin, name){
    email = cleanEmail(email);
    if(!email || !pin) return false;
    if(name){ try { if(!(LS.getItem(NAME_KEY) || '').trim()) LS.setItem(NAME_KEY, String(name).trim()); } catch(_){} }
    if(typeof W.hohYourHallSetSession === 'function'){
      try { W.hohYourHallSetSession(email, pin); return true; } catch(_){}
    }
    var old = (LS && readJSON(LS, TOKEN_KEY)) || {};
    var ok = writeJSON(LS, TOKEN_KEY, { email: email, pin: pin, savedAt: now(), phone: !!old.phone });
    try { LS.setItem(EMAIL_KEY, email); } catch(_){}
    try { D.dispatchEvent(new CustomEvent('hoh:signed-in', { detail: { source: 'google' } })); } catch(_){}
    return ok;
  }
  function pendingNew(){
    var n = LS && readJSON(LS, NEW_KEY);
    if(!n || !n.email || !/^g1\.[0-9a-f]{64}$/.test(String(n.pin || '')) || now() - (n.at || 0) > NEW_MS){ if(n) drop(LS, NEW_KEY); return null; }
    return n;
  }
  /* The submit flow takes the waiting session once the first hero is in (hoh-section-core.js). */
  function takeNew(email){
    var n = pendingNew();
    if(!n || n.email !== cleanEmail(email)) return null;
    drop(LS, NEW_KEY);
    return n;
  }

  function rpc(fn, body){
    return fetch(SB_URL + '/rest/v1/rpc/' + fn, { method: 'POST', credentials: 'omit',
      headers: { 'Content-Type': 'application/json', apikey: SB_KEY, Authorization: 'Bearer ' + SB_KEY }, body: JSON.stringify(body) })
      .then(function(r){ return r.json().then(function(j){ return { ok: r.ok, data: j }; }, function(){ return { ok: r.ok, data: null }; }); },
            function(){ return { ok: false, data: null }; });
  }
  /* A Google session sets (or resets) the account's 4-digit PIN on all its heroes (set_pin with the session as the old PIN). */
  function setPin(email, session, pin){
    return rpc('set_pin', { user_email: cleanEmail(email), old_pin: session, new_pin: pin }).then(function(r){ return r.ok && r.data === true; });
  }
  /* Has this email signed in with Google before? False when it can't tell (or the SQL isn't in yet). It is asked
     inside app browsers too: that is where a Google-only player needs telling where Google sign-in opens. */
  function hint(email){
    if(!W.fetch) return Promise.resolve(false);
    return rpc('hoh_google_hint', { p_email: cleanEmail(email) }).then(function(r){ return r.ok && r.data === true; });
  }

  /* ── results: whoever listens gets the last one, even if it arrived before they loaded ──────────────────── */
  var listeners = [], last = null;
  function onResult(fn){
    if(typeof fn !== 'function') return;
    listeners.push(fn);
    if(last && !last.seen){ last.seen = true; try { fn(last); } catch(_){} }
  }
  function publish(res){
    last = res;
    if(listeners.length){ res.seen = true; listeners.forEach(function(fn){ try { fn(res); } catch(_){} }); }
    try { D.dispatchEvent(new CustomEvent('hoh:google-result', { detail: res })); } catch(_){}
  }

  /* ── the "signing you in" layer while the Worker answers ─────────────────────────────────────────────────── */
  /* The button's own rules carry two classes, so a host's button reset (the hero page's .hhp button{background:none;
     border:0;padding:0}, one class and a tag) never strips its outline, fill or type; host width rules below still apply. */
  var CSS = ''
    + '.hgs-btn.hgs-btn{display:flex;align-items:center;justify-content:center;gap:10px;box-sizing:border-box;width:100%;max-width:400px;min-height:44px;margin:0 auto;padding:0 12px;border-radius:4px;'
    + 'font:500 14px/20px "Google Sans",Roboto,Arial,sans-serif;letter-spacing:.25px;cursor:pointer;-webkit-tap-highlight-color:transparent;transition:background-color .18s ease,box-shadow .18s ease}'
    + '.hgs-btn.hgs-dark{background:#131314;color:#E3E3E3;border:1px solid #8E918F}'
    + '.hgs-btn.hgs-dark:hover{background:#1f1f20;box-shadow:0 1px 3px rgba(0,0,0,.4)}'
    + '.hgs-btn.hgs-light{background:#fff;color:#1F1F1F;border:1px solid #747775}'
    + '.hgs-btn.hgs-light:hover{background:#f6f7f8;box-shadow:0 1px 3px rgba(60,64,67,.25)}'
    + '.hgs-btn.hgs-btn:focus-visible{outline:2px solid #e8c97a;outline-offset:2px}'
    + '.hgs-btn.hgs-btn[disabled]{opacity:.6;cursor:default}'
    + '.hgs-g{display:inline-flex;width:18px;height:18px;flex:0 0 18px}.hgs-g svg{width:18px;height:18px;display:block}'
    + '.hgs-slot:empty{display:none}.hgs-slot{display:block;margin:4px 0 0}'
    + '.hgs-or{display:flex;align-items:center;gap:12px;margin:14px 0 2px;font-size:12px;letter-spacing:.14em;text-transform:uppercase;opacity:.75}'
    + '.hgs-or::before,.hgs-or::after{content:"";flex:1;height:1px;background:currentColor;opacity:.4}'
    + '.hgs-note{margin:8px 0 0;font-size:13px;line-height:1.5;opacity:.85;text-align:center}'
    + '.hgs-lead{margin:14px 0 10px;font-size:14px;line-height:1.45;text-align:center}'
    /* the Hall's sign-in lightbox (hall-of-heroes.css) and the hero page (hoh-hero-page.css) */
    + '.hoh-yh-lb .hgs-or{color:#8a8296;font-family:"Cinzel",serif;font-size:10px}'
    + '.hoh-yh-lb .hgs-lead,.hoh-yh-lb .hgs-note{font-family:"Cormorant Garamond",Georgia,serif;color:#a89f92;font-size:15px}'
    + '.hoh-yh-lb .hgs-btn{max-width:none}.hoh-yh-lb .hoh-yh-lb-title+.hgs-slot{margin-top:16px}.hoh-yh-lb .hgs-slot+.hoh-yh-lb-lead{margin-top:10px}'
    + '.hhp .hgs-btn{max-width:none}.hhp .hgs-or{color:var(--ink3,#8C8494)}.hhp .hgs-lead,.hhp .hgs-note{color:var(--ink2,#B6AEBD)}'
    /* the add-hero form's last screen, above the name and email (hoh-section-core.js showFormView) */
    + '.hoh-form-google .hgs-slot{margin:0}.hoh-form-google .hgs-btn{max-width:none;min-height:48px}'
    + '.hoh-form-google .hgs-lead{margin:0 0 10px;font-size:14px;line-height:1.45;color:#b9b2c4}'
    + '.hoh-form-google .hgs-or{margin:18px 0 0;color:#8a8296}'
    /* the commission portal's "Bring in your older portraits" dialog (commission-portal-live.js olderDialog), on cream */
    + '.older-way .hgs-slot{margin:2px 0 0}.older-way .hgs-btn{max-width:none;min-height:48px}'
    + '.older-way .hgs-or{margin:14px 0 0;color:#756c63;opacity:1}'
    + '.hgs-wait{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;min-height:100svh;padding:16px;box-sizing:border-box;'
    + 'background:rgba(10,8,14,.72);-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);opacity:0;transition:opacity .2s ease}'
    + '.hgs-wait.hgs-on{opacity:1}'
    + '.hgs-wait-card{display:flex;align-items:center;gap:12px;padding:16px 20px;border-radius:12px;background:#17131f;color:#efe7d6;border:1px solid rgba(232,201,122,.35);'
    + 'font-family:inherit;font-size:15px;line-height:1.4;box-shadow:0 10px 40px rgba(0,0,0,.45)}'
    + '.hgs-done-card{flex-direction:column;max-width:340px;text-align:center;gap:16px}.hgs-done-card p{margin:0}'
    + '.hgs-spin{width:18px;height:18px;border-radius:50%;border:2px solid rgba(232,201,122,.3);border-top-color:#e8c97a;animation:hgs-spin .8s linear infinite}'
    + '@keyframes hgs-spin{to{transform:rotate(360deg)}}'
    + '@media (prefers-reduced-motion:reduce){.hgs-btn,.hgs-wait{transition:none}.hgs-spin{animation:none}}';
  function styles(){
    if(D.getElementById('hgs-style')) return;
    var s = D.createElement('style'); s.id = 'hgs-style'; s.textContent = CSS;
    (D.head || D.documentElement).appendChild(s);
  }
  var waitEl = null;
  function wait(on){
    if(on){
      styles();
      if(waitEl) return;
      waitEl = D.createElement('div');
      waitEl.className = 'hgs-wait';
      waitEl.setAttribute('role', 'status');
      waitEl.innerHTML = "<div class=\"hgs-wait-card\"><span class=\"hgs-spin\" aria-hidden=\"true\"></span><span>Signing in with Google</span></div>";
      (D.body || D.documentElement).appendChild(waitEl);
      requestAnimationFrame(function(){ if(waitEl) waitEl.classList.add('hgs-on'); });
    } else if(waitEl){
      var el = waitEl; waitEl = null;
      el.classList.remove('hgs-on');
      setTimeout(function(){ if(el.parentNode) el.parentNode.removeChild(el); }, 220);
    }
  }

  /* ── the button ──────────────────────────────────────────────────────────────────────────────────────────── */
  /* Google's standard multicolour G (sign-in branding guidelines: the G is never recoloured or used alone). */
  var G = '<svg viewBox="0 0 48 48" aria-hidden="true" focusable="false"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>'
    + '<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>'
    + '<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>'
    + '<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>';
  function buttonHTML(from, email, theme, reset, tab){
    return '<button type="button" class="hgs-btn ' + (theme === 'light' ? 'hgs-light' : 'hgs-dark') + '" data-hoh-google="' + esc(from) + '"'
      + (email ? ' data-hgs-email="' + esc(email) + '"' : '') + (reset ? ' data-hgs-reset="1"' : '') + (tab ? ' data-hgs-tab="1"' : '') + '>'
      + '<span class="hgs-g">' + G + '</span><span>Continue with Google</span></button>';
  }
  /* A host renders <div class="hgs-slot" data-hgs-from="hall" data-hgs-email="" data-hgs-theme="dark" data-hgs-or="after"
     data-hgs-reset="1" data-hgs-lead="..." data-hgs-inapp="note"></div> and calls hohGoogle.fill(root). The slot stays
     empty (and takes no room) where Google can't be used. data-hgs-or puts an "or" line after (or before) the button,
     data-hgs-lead a line above it; data-hgs-inapp="note" says, inside an app browser, where Google sign-in does open
     (hosts ask for it only for an email that signs in with Google). */
  function fillSlot(slot){
    var from = slot.getAttribute('data-hgs-from') || 'hall', email = slot.getAttribute('data-hgs-email') || '';
    var theme = slot.getAttribute('data-hgs-theme') || 'dark', or = slot.getAttribute('data-hgs-or') || '';
    var reset = slot.getAttribute('data-hgs-reset') === '1', tab = slot.getAttribute('data-hgs-tab') === '1';
    var lead = slot.getAttribute('data-hgs-lead') || '';
    if(available()){
      var orHTML = '<div class="hgs-or" aria-hidden="true">or</div>';
      slot.innerHTML = (or === 'before' ? orHTML : '') + (lead ? '<p class="hgs-lead">' + esc(lead) + '</p>' : '')
        + buttonHTML(from, email, theme, reset, tab) + (or === 'after' ? orHTML : '');
    } else if(inApp() && slot.getAttribute('data-hgs-inapp') === 'note'){
      slot.innerHTML = "<p class=\"hgs-note\">To use Google sign-in, open the &bull;&bull;&bull; menu and choose Safari or Chrome.</p>";
    } else {
      slot.innerHTML = '';
    }
    slot.setAttribute('data-hgs-done', '1');
  }
  function fill(root){
    root = root || D;
    var slots = root.querySelectorAll ? root.querySelectorAll('.hgs-slot') : [];
    if(!slots.length) return;
    styles();
    for(var i = 0; i < slots.length; i++) fillSlot(slots[i]);
    if(!available() && canTry()){
      ready().then(function(ok){ if(!ok) return; for(var j = 0; j < slots.length; j++) if(slots[j].isConnected) fillSlot(slots[j]); });
    }
  }
  /* Back from Google's page restores this one from the browser's memory, button still disabled: wake it up. */
  function wake(){
    var bs = D.querySelectorAll('[data-hoh-google]');
    for(var i = 0; i < bs.length; i++) bs[i].disabled = false;
  }
  W.addEventListener('pageshow', function(e){
    if(!e.persisted) return;
    starting = false;
    wake();
  });
  /* Capture phase: the add-hero form's box stops clicks from bubbling (onclick="event.stopPropagation()").
     A redirect keeps the button off until the page is left (pageshow wakes it on Back). A window of its own leaves this
     page where it is, so the button wakes up again a moment later: the visitor may close that window, or come back
     to try another account. */
  D.addEventListener('click', function(e){
    var b = e.target && e.target.closest ? e.target.closest('[data-hoh-google]') : null;
    if(!b || b.disabled) return;
    e.preventDefault();
    b.disabled = true;
    var tab = b.getAttribute('data-hgs-tab') === '1';
    start({ from: b.getAttribute('data-hoh-google'), email: b.getAttribute('data-hgs-email') || '', reset: b.getAttribute('data-hgs-reset') === '1', tab: tab })
      .then(function(went){ if(!went) b.disabled = false; else if(tab) setTimeout(function(){ b.disabled = false; }, 1500); });
  }, true);

  /* ── coming back from Google ─────────────────────────────────────────────────────────────────────────────── */
  function takeReturn(){
    var u;
    try { u = new URL(location.href); } catch(_){ return null; }
    var state = u.searchParams.get('state'), code = u.searchParams.get('code'), err = u.searchParams.get('error');
    if(!state || (!code && !err)) return null;
    var p = LS && readJSON(LS, PENDING_KEY);
    if(!p) return null;                           /* nothing of ours is waiting: not our sign-in, leave the address alone */
    try { history.replaceState(history.state, '', addressWithout(RETURN_PARAMS)); } catch(_){}
    /* an older Google window finishing after a newer sign-in began: what waits is the newer one's, so it stays for it */
    if(p.state !== state){
      if(now() - (p.at || 0) > PENDING_MS){ drop(LS, PENDING_KEY); return { p: p, reason: 'stale' }; }
      return { p: p, reason: 'state', stray: true };
    }
    drop(LS, PENDING_KEY);
    if(now() - (p.at || 0) > PENDING_MS) return { p: p, reason: 'stale' };
    if(err) return { p: p, reason: err === 'access_denied' ? 'cancel' : 'google_' + String(err).slice(0, 40) };
    return { p: p, code: code };
  }
  function exchange(ret){
    var p = ret.p;
    var base = { from: p.from, reset: !!p.reset };
    if(!ret.code) return Promise.resolve(Object.assign(base, { ok: false, reason: ret.reason, message: message(ret.reason) }));
    return fetch(WORKER + '/session', { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: ret.code, verifier: p.verifier, redirect_uri: p.page, nonce: p.nonce }) })
      .then(function(r){ return r.json().catch(function(){ return { ok: false, reason: 'server' }; }); }, function(){ return { ok: false, reason: 'network' }; })
      .then(function(j){
        if(!j || j.ok !== true || !/^g1\.[0-9a-f]{64}$/.test(String(j.pin || '')) || !j.email){
          var reason = (j && j.reason) || 'server';
          return Object.assign(base, { ok: false, reason: reason, message: message(reason) });
        }
        var res = Object.assign(base, { ok: true, email: cleanEmail(j.email), pin: j.pin, heroes: +j.heroes || 0, has_pin: j.has_pin === true,
          player_name: j.player_name || '', google_name: j.google_name || '' });
        if(res.heroes > 0){
          drop(LS, NEW_KEY);
          saveSession(res.email, res.pin, res.player_name);
        } else {
          writeJSON(LS, NEW_KEY, { email: res.email, pin: res.pin, name: res.google_name, at: now() });
        }
        return res;
      });
  }
  function boot(){
    var ret = takeReturn();
    if(!ret){
      /* a result carried across the hop back to the page the sign-in began on */
      var carried = SS && readJSON(SS, RESULT_KEY);
      if(carried){ drop(SS, RESULT_KEY); if(now() - (carried.at || 0) < 60e3) publish(carried); }
      return;
    }
    if(ret.stray){
      D.documentElement.classList.remove('hgs-returning');
      publish({ ok: false, reason: 'state', message: message('state'), from: ret.p.from });
      return;
    }
    wait(true);
    exchange(ret).then(function(res){
      res.at = now();
      if(ret.p.tab){
        /* a Google window opened by another page: the answer goes back to that page, and this window closes */
        delete res.pin;
        writeJSON(LS, DONE_KEY, Object.assign({}, res, { state: ret.p.state }));
        wait(false);
        D.documentElement.classList.remove('hgs-returning');
        try { W.close(); } catch(_){}
        setTimeout(function(){ doneNote(res); }, 400);
        return;
      }
      /* the sign-in began somewhere else (a hero page's own address): go back there with the result */
      var back = ret.p.ret || '';
      var here = location.origin + location.pathname + location.search;
      var backNoHash = back.split('#')[0];
      if(back && backNoHash !== here && backNoHash.indexOf(location.origin + '/') === 0){
        delete res.pin;
        writeJSON(SS, RESULT_KEY, res);
        location.replace(back);
        return;
      }
      wait(false);
      D.documentElement.classList.remove('hgs-returning');
      delete res.pin;
      publish(res);
    }, function(){
      wait(false);
      D.documentElement.classList.remove('hgs-returning');
      publish({ ok: false, reason: 'server', message: message('server'), from: ret.p.from });
    });
  }

  /* Still open after the hand-back (a browser keeps a window it did not open itself): say where to go. */
  function doneNote(res){
    styles();
    var el = D.createElement('div');
    el.className = 'hgs-wait hgs-on';
    el.setAttribute('role', 'status');
    el.innerHTML = '<div class="hgs-wait-card hgs-done-card"><p>' + (res.ok ? "You’re signed in with Google. Return to your other tab to continue." : esc(res.message || message(res.reason))) + '</p>'
      + '<button type="button" class="hgs-btn hgs-dark" data-hgs-close>Close this tab</button></div>';
    (D.body || D.documentElement).appendChild(el);
    el.querySelector('[data-hgs-close]').addEventListener('click', function(){ try { W.close(); } catch(_){} });
  }

  W.hohGoogle = {
    available: available, ready: ready, fill: fill, start: start, onResult: onResult, pendingNew: pendingNew, takeNew: takeNew,
    saveSession: saveSession, setPin: setPin, hint: hint, inApp: inApp, message: message,
    sessionFor: function(email){
      try { var t = readJSON(LS, TOKEN_KEY); return (t && cleanEmail(t.email) === cleanEmail(email) && /^g1\./.test(String(t.pin || ''))) ? t.pin : ''; } catch(_){ return ''; }
    }
  };

  boot();
  /* Signed-out visitors on a page with a sign-in: fetch the settings while the page is idle, so the button is
     there the first time the sign-in opens instead of appearing a moment later. */
  try {
    var t = LS && readJSON(LS, TOKEN_KEY);
    if(!(t && t.email && t.pin) && canTry() && !cachedCfg()){
      (W.requestIdleCallback || function(f){ return setTimeout(f, 1500); })(function(){ config(); });
    }
  } catch(_){}
})();
